// What the installed app shows without a connection (M15, D-140): a screen when it opens offline (the
// session cannot be restored until the network is back), and inside the app a bar saying it is offline
// and how many notes wait on this device; they are sent, in order, as soon as the connection returns.
import { useQueryClient } from '@tanstack/react-query';
import { Button, toast } from '@tatra/ui';
import { CloudOff, RefreshCw } from 'lucide-react';
import { useEffect } from 'react';
import { replay, useOnline, useQueued } from '../../lib/offline-queue.js';

export function OfflineScreen() {
  return (
    <main
      className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-bg px-6 text-center"
      data-testid="offline-screen"
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-surface text-text-muted shadow-soft">
        <CloudOff className="h-7 w-7" aria-hidden="true" />
      </span>
      <div className="max-w-sm space-y-1.5">
        <h1 className="font-display text-xl font-semibold text-text">You are offline</h1>
        <p className="text-sm text-text-muted">
          Tatra CRM will pick up where you left off as soon as your phone is back online. Notes you saved
          offline are kept on this device until then.
        </p>
      </div>
      <Button variant="secondary" onClick={() => window.location.reload()}>
        <RefreshCw className="h-4 w-4" aria-hidden="true" /> Try again
      </Button>
    </main>
  );
}

/** The offline bar inside the app; sends the member's waiting notes when the connection returns. */
export function OfflineBar({ memberId }: { memberId: string }) {
  const online = useOnline();
  const queued = useQueued(memberId);
  const qc = useQueryClient();

  useEffect(() => {
    if (!online || queued.length === 0) return;
    void replay(memberId).then(({ sent, dropped }) => {
      if (sent) {
        toast.success(sent === 1 ? 'Your offline note was sent' : `${sent} offline notes were sent`);
        void qc.invalidateQueries();
      }
      for (const d of dropped) toast.error(`Could not send “${d.label}”: it was refused`);
    });
    // Re-run when the connection returns or something new is queued.
  }, [online, queued.length, memberId, qc]);

  if (online && queued.length === 0) return null;
  return (
    <div
      role="status"
      data-testid="offline-bar"
      className="mb-4 flex items-center gap-2 rounded-xl border border-warning/40 bg-warning-bg px-3 py-2 text-sm text-text"
    >
      <CloudOff className="h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        {online ? 'Sending what you saved offline…' : 'You are offline.'}
        {queued.length > 0 &&
          ` ${queued.length === 1 ? '1 note is' : `${queued.length} notes are`} waiting on this device.`}
      </span>
    </div>
  );
}
